import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { policz } from '../../shared/pl.ts';
import { Blad, Odznaka, Panel, Pusto, Tabela, type Kolumna } from '../ui/index.tsx';

/**
 * Stan zleceń: na jakim etapie jest produkcja.
 *
 * Sedno tego ekranu to odpowiedź na pytanie „chłodnictwo zrobione, elektryka
 * w trakcie?". Sam licznik rozpoczętych sztuk tego nie mówi — zlecenie nie idzie
 * jednym frontem, więc etap pokazujemy per operacja.
 */

interface Etap {
  operation_code: string; operation_name: string; station_code: string;
  rozpoczete: number; zakonczone: number; stan: 'nie_rozpoczete' | 'w_trakcie' | 'zakonczone';
  norma_h?: number | null; wMarszrucie?: boolean; ostatnia?: string | null; loginy?: string | null;
}
interface OrderRow {
  zp_number: string; product_code: string | null; product_name: string | null;
  customer: string | null; qty: number; serial_count: number; started_count: number;
  finished_ops: number; last_activity: string | null; etapy: Etap[];
}
interface Szczegol { order: OrderRow; sztuk: number; etapy: Etap[]; plantTime: string }

const BARWA: Record<Etap['stan'], string> = {
  zakonczone: 'bg-ok',
  w_trakcie: 'bg-plum',
  nie_rozpoczete: 'bg-line',
};
const OPIS: Record<Etap['stan'], string> = {
  zakonczone: 'zakończone',
  w_trakcie: 'w trakcie',
  nie_rozpoczete: 'nie rozpoczęte',
};

export default function Orders() {
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [plantTime, setPlantTime] = useState('');
  const [q, setQ] = useState('');
  const [tylkoWToku, setTylkoWToku] = useState(false);
  const [laduje, setLaduje] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [wybrany, setWybrany] = useState<OrderRow | null>(null);
  const [szczegol, setSzczegol] = useState<Szczegol | null>(null);
  const nav = useNavigate();

  const load = () => {
    setLaduje(true);
    setBlad(null);
    api.get<{ orders: OrderRow[]; plantTime: string }>('/reports/orders?limit=100')
      .then((r) => { setRows(r.orders); setPlantTime(r.plantTime); })
      .catch((e) => setBlad(e.message))
      .finally(() => setLaduje(false));
  };
  useEffect(load, []);

  const otworz = async (r: OrderRow) => {
    setWybrany(r);
    setSzczegol(null);
    try {
      setSzczegol(await api.get<Szczegol>(`/orders/${encodeURIComponent(r.zp_number)}/etapy`));
    } catch { /* panel pokaże sam komunikat o braku danych */ }
  };

  const igla = q.trim().toLowerCase();
  const widoczne = useMemo(() => rows.filter((r) => {
    if (tylkoWToku && r.started_count === 0) return false;
    if (!igla) return true;
    return r.zp_number.toLowerCase().includes(igla)
      || (r.product_name ?? '').toLowerCase().includes(igla)
      || (r.product_code ?? '').toLowerCase().includes(igla)
      || (r.customer ?? '').toLowerCase().includes(igla);
  }), [rows, igla, tylkoWToku]);

  const kolumny: Array<Kolumna<OrderRow>> = [
    { klucz: 'zp', naglowek: 'Zlecenie', sort: (r) => r.zp_number, szer: '170px',
      render: (r) => <span className="font-semibold text-plum whitespace-nowrap">{r.zp_number}</span> },
    { klucz: 'wyrob', naglowek: 'Agregat', sort: (r) => r.product_name ?? '',
      render: (r) => (
        <span>
          <span className="block truncate max-w-[260px]">{r.product_name ?? '—'}</span>
          {r.customer && <span className="block text-caption text-plum-muted truncate max-w-[260px]">{r.customer}</span>}
        </span>
      ) },
    { klucz: 'etapy', naglowek: 'Etapy',
      render: (r) => (
        <span className="flex flex-wrap gap-1">
          {r.etapy.length === 0 && <span className="text-caption text-plum-muted">brak pracy</span>}
          {r.etapy.map((e) => (
            <span key={e.operation_code}
                  title={`${e.operation_name}: ${OPIS[e.stan]} (${e.zakonczone}/${r.serial_count} szt.)`}
                  className={e.stan === 'zakonczone' ? 'badge-ok' : 'badge-info'}>
              {e.operation_code}
              <span className="opacity-70 tnum">{e.zakonczone}/{r.serial_count}</span>
            </span>
          ))}
        </span>
      ) },
    { klucz: 'postep', naglowek: 'Sztuk rozpoczętych', prawo: true, szer: '150px',
      sort: (r) => (r.serial_count ? r.started_count / r.serial_count : 0),
      render: (r) => (
        <span className="inline-flex items-center gap-2 justify-end w-full">
          <span className="hidden sm:block w-16 meter">
            <span className="bg-plum" style={{ width: `${r.serial_count ? (r.started_count / r.serial_count) * 100 : 0}%` }} />
          </span>
          <span className="tnum whitespace-nowrap">{r.started_count}/{r.serial_count}</span>
        </span>
      ) },
    { klucz: 'ostatnia', naglowek: 'Ostatnia praca', prawo: true, szer: '120px',
      sort: (r) => r.last_activity ?? '',
      render: (r) => <span className="tnum text-plum-muted whitespace-nowrap">{r.last_activity ?? '—'}</span> },
  ];

  if (blad) return <Blad tresc={blad} ponow={load} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-head-sm">Zlecenia</h1>
        <span className="text-body-sm text-plum-muted">{policz(widoczne.length, 'zlecenie')}</span>
        <span className="ml-auto text-caption text-plum-muted">dane na {plantTime} · czas zakładowy</span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          className="field !w-auto flex-1 min-w-[220px]"
          placeholder="Filtruj po ZP, agregacie lub odbiorcy"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Filtr zleceń"
        />
        <label className="tap inline-flex items-center gap-2 text-body-sm text-plum-soft
                          min-h-[40px] px-3 border border-line-strong rounded-lg bg-white cursor-pointer">
          <input type="checkbox" checked={tylkoWToku} onChange={(e) => setTylkoWToku(e.target.checked)} />
          Tylko rozpoczęte
        </label>
        {(q || tylkoWToku) && (
          <button className="btn-quiet" onClick={() => { setQ(''); setTylkoWToku(false); }}>Wyczyść filtry</button>
        )}
      </div>

      <Tabela
        dane={widoczne}
        kolumny={kolumny}
        klucz={(r) => r.zp_number}
        laduje={laduje}
        onKlik={otworz}
        wybrany={(r) => r.zp_number === wybrany?.zp_number}
        pusto={<Pusto tytul="Brak zleceń"
                      opis="Żadne zlecenie nie pasuje do filtra albo baza numerów seryjnych jest pusta." />}
      />

      <p className="text-caption text-plum-muted">
        Etap liczony osobno dla każdej operacji, bo zlecenie nie idzie jednym frontem —
        sztuka może mieć zamknięty montaż chłodniczy i nierozpoczętą izolację.
        Kliknij wiersz, żeby zobaczyć pełną marszrutę.
      </p>

      {wybrany && (
        <Panel
          tytul={wybrany.zp_number}
          pod={[wybrany.product_name, wybrany.customer].filter(Boolean).join(' · ') || undefined}
          onZamknij={() => { setWybrany(null); setSzczegol(null); }}
        >
          {!szczegol ? (
            <p className="text-body-sm text-plum-muted" role="status">Wczytywanie etapów…</p>
          ) : (
            <Marszruta d={szczegol} onPraca={() => nav(`/praca?q=${encodeURIComponent(wybrany.zp_number)}`)} />
          )}
        </Panel>
      )}
    </div>
  );
}

/** Pełna marszruta jednego zlecenia: wszystkie operacje wyrobu w kolejności. */
function Marszruta({ d, onPraca }: { d: Szczegol; onPraca: () => void }) {
  const zrobione = d.etapy.filter((e) => e.stan === 'zakonczone').length;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-label uppercase text-plum-muted mb-1.5">Sztuk</div>
          <div className="kpi-sm">{d.sztuk}</div>
        </div>
        <div>
          <div className="text-label uppercase text-plum-muted mb-1.5">Operacje</div>
          <div className="kpi-sm">{zrobione}/{d.etapy.length}</div>
          <div className="text-caption text-plum-muted mt-1">zakończonych</div>
        </div>
        <div>
          <div className="text-label uppercase text-plum-muted mb-1.5">Postęp</div>
          <div className="kpi-sm">
            {d.etapy.length ? Math.round((zrobione / d.etapy.length) * 100) : 0}%
          </div>
        </div>
      </div>

      <div className="space-y-2 pt-4 border-t border-line">
        {d.etapy.length === 0 && (
          <Pusto tytul="Brak marszruty" opis="Dla tego wyrobu nie ma jeszcze żadnej normy ani zarejestrowanej pracy." />
        )}
        {d.etapy.map((e) => {
          const udzial = d.sztuk > 0 ? e.zakonczone / d.sztuk : 0;
          return (
            <div key={e.operation_code}>
              <div className="flex items-baseline gap-2">
                <span className="text-body-sm font-semibold text-plum">{e.operation_name}</span>
                {e.wMarszrucie === false && <Odznaka ton="warn">poza marszrutą</Odznaka>}
                <span className={`ml-auto text-caption ${
                  e.stan === 'zakonczone' ? 'text-ok' : e.stan === 'w_trakcie' ? 'text-plum-soft' : 'text-plum-muted'}`}>
                  {OPIS[e.stan]}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="meter flex-1">
                  <span className={BARWA[e.stan]}
                        style={{ width: `${Math.max(udzial * 100, e.stan === 'w_trakcie' ? 4 : 0)}%` }} />
                </div>
                <span className="w-20 shrink-0 text-caption text-right tnum text-plum-muted">
                  {e.zakonczone}/{d.sztuk} szt.
                </span>
              </div>
              {e.loginy && (
                <p className="mt-1 text-caption text-plum-muted tnum truncate">pracowali: {e.loginy}</p>
              )}
            </div>
          );
        })}
      </div>

      {d.etapy.some((e) => e.wMarszrucie === false) && (
        <p className="text-caption text-warn border-t border-line pt-3">
          Operacje oznaczone jako „poza marszrutą" mają zarejestrowaną pracę, ale nie mają
          normy dla tego wyrobu — albo norma nie została uzupełniona, albo pracę zapisano omyłkowo.
        </p>
      )}

      <button className="btn-ghost w-full" onClick={onPraca}>Pokaż wpisy tego zlecenia →</button>
    </div>
  );
}
